import type { AudioVideoEventAttributes, EventName } from 'amazon-chime-sdk-js';
import { Subject } from 'rxjs';

import type { HearingPhoneWithDisconnectReason } from 'features/call/call-hearing/interfaces';
import type { TranscriptCaption } from 'features/voice-meeting/interfaces';
import type { CallDirection } from 'features/voice-session/interfaces';

export interface AttendeeIdPresence {
  attendeeId: string;
  present: boolean;
  externalUserId?: string;
  dropped?: boolean;
}

export enum VoiceSessionCleanupIntent {
  FinishAndDeleteVoiceSession = 'finish-and-delete-voice-session',
  ResetLocalSession = 'reset-local-session',
  DetachLocalSession = 'detach-local-session',
}

export class VoiceMeetingEventBus {
  static hearingCallDeclined$ = new Subject<HearingPhoneWithDisconnectReason>();

  static fatalError$ = new Subject<Error>();

  static voiceSessionCleanup$ = new Subject<VoiceSessionCleanupIntent>();

  static attendeeIdPresence$ = new Subject<AttendeeIdPresence>();
  static meetingError$ = new Subject<{
    name: EventName;
    attributes: AudioVideoEventAttributes;
  }>();

  static meetingStatusChange$ = new Subject<string>();

  static callDirectionChanged$ = new Subject<CallDirection>();

  static transcriptCaption$ = new Subject<TranscriptCaption>();

  // Bumped to abandon in-flight/queued cleanups after fail-open reconnect.
  private static voiceSessionCleanupGeneration = 0;

  static readonly getVoiceSessionCleanupGeneration = () =>
    VoiceMeetingEventBus.voiceSessionCleanupGeneration;

  static readonly invalidatePendingVoiceSessionCleanup = () => {
    VoiceMeetingEventBus.voiceSessionCleanupGeneration += 1;
  };

  static readonly isVoiceSessionCleanupCurrent = (generation: number) =>
    generation === VoiceMeetingEventBus.voiceSessionCleanupGeneration;

  private static readonly emitVoiceSessionCleanup = (
    intent: VoiceSessionCleanupIntent
  ) => {
    VoiceMeetingEventBus.voiceSessionCleanup$.next(intent);
  };

  // Full teardown: stop the local client and delete the backend voice session.
  static readonly finishAndDeleteVoiceSession = () => {
    VoiceMeetingEventBus.emitVoiceSessionCleanup(
      VoiceSessionCleanupIntent.FinishAndDeleteVoiceSession
    );
  };

  // Local-only teardown after a reset, transfer, or completed delete attempt.
  // The shared backend voice session is not deleted here.
  static readonly resetLocalSession = () => {
    VoiceMeetingEventBus.emitVoiceSessionCleanup(
      VoiceSessionCleanupIntent.ResetLocalSession
    );
  };

  // Local-only teardown when this client should detach without deleting the
  // shared backend voice session, such as teaming leave or AST SignMail.
  static readonly detachLocalSession = () => {
    VoiceMeetingEventBus.emitVoiceSessionCleanup(
      VoiceSessionCleanupIntent.DetachLocalSession
    );
  };
}
